import type { CalendarDate } from '@internationalized/date';
import {
  CurrencyCode,
  enumPaymentMethod,
  FinancialAccountType,
  MoneyInput,
  PaymentAllocationMethod,
  PaymentMethod,
} from '@rematter/frontend-lib-api-client';
import type { DateValue } from '@rematter/ui-web';
import { atom } from 'jotai';

import { now } from '~/shared/utils/dateTime';

export const PAYMENT_METHOD_ACCOUNT_TYPES: Partial<
  Record<PaymentMethod, FinancialAccountType[]>
> = {
  Check: ['Checking'],
  ACH: ['Checking'],
  Wire: ['Checking', 'Savings'],
  Cash: ['Cash'],
  ATM: ['ATM'],
  WriteOff: ['WriteOff'],
};

export interface PaymentItem {
  id: string;
  amount: MoneyInput;
  dueDate: CalendarDate | null;
  accountId: string | null;
  accountName: string;
  facilityId: string;
  documentNumber: string;
}

export interface AccountGroup<T extends PaymentItem> {
  accountKey: string;
  name: string;
  total: number;
  items: T[];
  currencyCode: CurrencyCode;
}

// 🔘🔘🔘 PLAIN ATOMS — local UI state
export const $paymentItems = atom<PaymentItem[]>([]);
export const $paymentMethod = atom<PaymentMethod>(enumPaymentMethod.ACH);
export const $transactionDate = atom<DateValue | null>(now());
export const $financialAccountId = atom<string | null>(null);
export const $checkFacilityId = atom<string | null>(null);
export const $checkNumber = atom<string>('');
export const $checkMemo = atom<string>('');
export const $achNumber = atom<string | null>(null);
export const $wireNumber = atom<string | null>(null);
export const $groupWireNumbers = atom<Map<string, string>>(new Map());
export const $isSubmitting = atom<boolean>(false);
export const $selectedAllocationMethod = atom<PaymentAllocationMethod | null>(
  null,
);
export const $showAllocationBanner = atom<boolean>(false);
export const $itemAllocations = atom<Map<string, number>>(new Map());
export const $unknownAccountLabel = atom<string>('Unknown Account');

// 🟢🟢🟢 COMPUTED DATA — Sum of all payment item amounts
export const $totalAmount = atom((get) => {
  const items = get($paymentItems);
  return items.reduce((acc, item) => acc + item.amount.amount, 0);
});

// 🟢🟢🟢 COMPUTED DATA — Groups payment items by account, aggregating totals per account
export const $accountGroups = atom((get) => {
  const items = get($paymentItems);
  const unknownLabel = get($unknownAccountLabel);
  const map = new Map<string, AccountGroup<PaymentItem>>();
  for (const item of items) {
    const key = item.accountId ?? '__no_account__';
    const existing = map.get(key);
    if (existing) {
      existing.total += item.amount.amount;
      existing.items.push(item);
    } else {
      map.set(key, {
        accountKey: key,
        name: item.accountName || unknownLabel,
        total: item.amount.amount,
        items: [item],
        currencyCode: item.amount.currencyCode,
      });
    }
  }
  return [...map.values()];
});

// 🟢🟢🟢 COMPUTED DATA — Whether items span multiple currencies (blocks submission)
export const $hasMixedCurrencies = atom((get) => {
  const items = get($paymentItems);
  return new Set(items.map((i) => i.amount.currencyCode)).size > 1;
});

// 🟢🟢🟢 COMPUTED DATA — Distinct facility IDs from payment items
export const $facilityIDs = atom((get) => {
  const items = get($paymentItems);
  const ids = [...new Set(items.map((i) => i.facilityId))];
  return ids.length > 0 ? ids : undefined;
});

// 🟢🟢🟢 COMPUTED DATA — Summed allocation per account group from individual item allocations
export const $accountAllocations = atom((get) => {
  const groups = get($accountGroups);
  const itemAllocs = get($itemAllocations);
  const map = new Map<string, number>();
  for (const group of groups) {
    const sum = group.items.reduce(
      (acc, item) => acc + (itemAllocs.get(item.id) ?? 0),
      0,
    );
    map.set(group.accountKey, sum);
  }
  return map;
});

// 🟢🟢🟢 COMPUTED DATA — Total allocated payment amount across all items
export const $paymentAmount = atom((get) => {
  const itemAllocs = get($itemAllocations);
  return [...itemAllocs.values()].reduce((sum, v) => sum + v, 0);
});

// 🟢🟢🟢 COMPUTED DATA — Validates all required fields are set and payment amount is within bounds
export const $canSubmit = atom((get) => {
  const method = get($paymentMethod);
  if (method === 'ACH' && !get($achNumber)) return false;
  return (
    get($transactionDate) != null &&
    get($financialAccountId) != null &&
    get($paymentAmount) > 0 &&
    get($paymentAmount) <= get($totalAmount) &&
    !get($hasMixedCurrencies)
  );
});

// 🟠🟠🟠 ACTION — Sets the allocation amount for a single item
export const $updateItemAllocation = atom(
  null,
  (_get, set, { itemId, value }: { itemId: string; value: number }) => {
    set($itemAllocations, (prev) => {
      const next = new Map(prev);
      next.set(itemId, value);
      return next;
    });
  },
);

// 🟠🟠🟠 ACTION — Distributes an account-level allocation proportionally across its items
export const $updateAccountAllocation = atom(
  null,
  (get, set, { accountKey, value }: { accountKey: string; value: number }) => {
    const groups = get($accountGroups);
    const group = groups.find((g) => g.accountKey === accountKey);
    if (!group) return;
    const ratio = group.total > 0 ? value / group.total : 0;
    set($itemAllocations, (prev) => {
      const next = new Map(prev);
      let distributed = 0;
      for (let i = 0; i < group.items.length; i++) {
        if (i === group.items.length - 1) {
          next.set(
            group.items[i].id,
            Math.max(0, Math.round((value - distributed) * 100) / 100),
          );
        } else {
          const itemAllocation =
            Math.round(group.items[i].amount.amount * ratio * 100) / 100;
          next.set(group.items[i].id, itemAllocation);
          distributed += itemAllocation;
        }
      }
      return next;
    });
  },
);

// 🟠🟠🟠 ACTION — Applies FIFO or LIFO allocation by sorting items by due date within each group
export const $applyAllocationMethod = atom(null, (get, set) => {
  const method = get($selectedAllocationMethod);
  if (!method) return;
  const groups = get($accountGroups);
  set($itemAllocations, (prev) => {
    const next = new Map(prev);
    for (const group of groups) {
      const groupAllocation = group.items.reduce(
        (acc, item) => acc + (prev.get(item.id) ?? 0),
        0,
      );
      const sorted = [...group.items].sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return method === 'FIFO'
          ? a.dueDate.compare(b.dueDate)
          : b.dueDate.compare(a.dueDate);
      });
      let remaining = groupAllocation;
      for (const item of sorted) {
        const amount = Math.min(remaining, item.amount.amount);
        next.set(item.id, amount);
        remaining -= amount;
      }
    }
    return next;
  });
  set($showAllocationBanner, false);
});

// 🟠🟠🟠 ACTION — Handles financial account selection; shows allocation banner for non-weighted methods
export const $updateAccountSelection = atom(
  null,
  (
    _get,
    set,
    account: {
      id: string;
      name: string;
      paymentAllocationMethod: PaymentAllocationMethod;
    } | null,
  ) => {
    if (account && account.paymentAllocationMethod !== 'WeightedDistribution') {
      set($selectedAllocationMethod, account.paymentAllocationMethod);
      set($showAllocationBanner, true);
    } else {
      set($selectedAllocationMethod, null);
      set($showAllocationBanner, false);
    }
  },
);

// 🟠🟠🟠 ACTION — Switches payment method and resets dependent account/allocation state
export const $updatePaymentMethod = atom(
  null,
  (_get, set, value: PaymentMethod | undefined) => {
    if (value) {
      set($paymentMethod, value);
      set($financialAccountId, null);
      set($selectedAllocationMethod, null);
      set($showAllocationBanner, false);
    }
  },
);

// 🟠🟠🟠 ACTION — Sets or removes a wire reference number for a specific account group
export const $updateGroupWireNumber = atom(
  null,
  (
    _get,
    set,
    { accountKey, value }: { accountKey: string; value: string | null },
  ) => {
    set($groupWireNumbers, (prev) => {
      const next = new Map(prev);
      if (value) {
        next.set(accountKey, value);
      } else {
        next.delete(accountKey);
      }
      return next;
    });
  },
);

// 🟠🟠🟠 ACTION — Initializes items and sets default allocations to each item's full amount
export const $initPaymentItems = atom(
  null,
  (
    _get,
    set,
    { items, unknownLabel }: { items: PaymentItem[]; unknownLabel: string },
  ) => {
    set($paymentItems, items);
    set($unknownAccountLabel, unknownLabel);
    set($itemAllocations, new Map(items.map((i) => [i.id, i.amount.amount])));
  },
);

// 🟠🟠🟠 ACTION — Resets all payment state to defaults with full item allocations
export const $resetPaymentAllocation = atom(null, (get, set) => {
  const items = get($paymentItems);
  set($paymentMethod, enumPaymentMethod.ACH);
  set($transactionDate, now());
  set($financialAccountId, null);
  set($checkFacilityId, null);
  set($checkNumber, '');
  set($checkMemo, '');
  set($achNumber, null);
  set($wireNumber, null);
  set($groupWireNumbers, new Map());
  set($selectedAllocationMethod, null);
  set($showAllocationBanner, false);
  set($isSubmitting, false);
  set($itemAllocations, new Map(items.map((i) => [i.id, i.amount.amount])));
});
